import { ChangeDetectorRef, Component, Input, LOCALE_ID, OnInit, inject } from '@angular/core'
import { Materiel } from '../../models/materiel'
import { PhotoService } from '../../services/photo.service'
import { Router, RouterLink } from '@angular/router'
import { EvenementService } from '../../services/evenement.service'
import { ToastrService } from 'ngx-toastr'
import { MaterielService } from '../../services/materiel.service'
import { downloadPdfFile, reloadRoute, scrollToTop } from '../../utils/utils'
import dayjs from 'dayjs'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { MaterielReceptionModalComponent } from '../materiel-reception-modal/materiel-reception-modal'
import { MaterielLightDto } from '../../models/materiel-light.dto'
import { EvenementDto } from '../../models/evenement-dto'
import { ReferenceService } from '../../services/reference.service'
import { DataSharedService } from '../../services/data-shared.service'
import { NgClass, DatePipe } from '@angular/common';
import { UniteTempsLabelPipe } from '../../pipes/unite-temps-label.pipe';
import { Subscription } from 'rxjs';

@Component({
    selector: 'lg-materiel-card',
    templateUrl: './materiel-card.component.html',
    styleUrls: ['./materiel-card.component.scss'],
    imports: [RouterLink, NgClass, DatePipe, UniteTempsLabelPipe]
})
export class MaterielCardComponent implements OnInit {
  private router = inject(Router);
  private photoService = inject(PhotoService);
  private referenceService = inject(ReferenceService);
  private evenementService = inject(EvenementService);
  private dataSharedService = inject(DataSharedService);
  private materielService = inject(MaterielService);
  private locale = inject(LOCALE_ID);
  private toastr = inject(ToastrService);
  private cdr = inject(ChangeDetectorRef);
  modalService = inject(NgbModal);

  @Input() chantierCode: string

  private _materiel: MaterielLightDto
  @Input()
  set materiel(value: MaterielLightDto) {
    this._materiel = value
    this.initCardPhoto()
    this.updateDernierEvenementCSS()
  }
  get materiel(): MaterielLightDto {
    return this._materiel
  }

  downloadingPdf = false
  pointerUntilTodayLoading = false

  materielPhotoUrl: string = null
  dernierEvenementCSS: string | null = null
  private photoSubscription?: Subscription

  ngOnInit() {
    this.initCardPhoto()
  }

  pointerUntilToday() {
    if (dayjs().isSame(this.materiel.dateDernierEvenement, 'day')) {
      this.toastr.warning("Matériel déjà pointé jusqu'à aujourd'hui")
      return
    }

    this.pointerUntilTodayLoading = true
    this.cdr.detectChanges()

    this.evenementService
      .createEvenementPointageToday(this.chantierCode, this.materiel.id)
      .subscribe((evenementDto: EvenementDto) => {
        setTimeout(() => {
          this.materiel.dateDernierEvenement = evenementDto.date
          this.pointerUntilTodayLoading = false
          this.updateDernierEvenementCSS()
          this.toastr.success("Matériel pointé jusqu'à aujourd'hui")
          this.cdr.detectChanges()
        })
      })
  }

  endLocation() {
    this.router.navigate(['/chantiers', this.chantierCode, 'materiels', this.materiel.id, 'pointage'], {
      queryParams: {
        typePointageToPreselect: 'SRT',
        date: dayjs(new Date(dayjs().format('YYYY-MM-DD'))),
        from: 'materiel-card'
      },
    })
  }

  public downloadPdf() {
    this.downloadingPdf = true
    this.cdr.detectChanges()
    this.materielService.downloadMaterielPdf(this.chantierCode, this.materiel.id).subscribe({
      next: (response) => {
        downloadPdfFile(response)
        this.downloadingPdf = false
        this.toastr.success('PDF téléchargé')
        this.cdr.detectChanges()
      },
      error: (err) => {
        this.downloadingPdf = false
        this.toastr.error(err)
        this.cdr.detectChanges()
      },
    })
  }

  public reactiverMateriel() {
    // L'événement de sortie est supprimé
    this.evenementService
      .deleteEvenement(this.chantierCode, this.materiel.id, this.materiel.idEvenementSortie)
      .subscribe({
        next: () => {
          // La liste de l'onglet Historique est rafraîchie pour ne plus faire apparaître ce matériel
          reloadRoute(this.router, ['chantiers', this.chantierCode, 'historique'])
          scrollToTop()
          this.toastr.success('Matériel réactivé')
          this.modalService.dismissAll()
        },
      })
  }

  archiver() {
    this.materielService.archiveMateriel(this.chantierCode, this.materiel.id, true).subscribe((archivedMateriel) => {
      this.loadMaterielInCard(archivedMateriel)
      this.toastr.success('Matériel archivé')
      reloadRoute(this.router, ['chantiers', this.chantierCode, 'a-receptionner'])
    })
  }

  desarchiver() {
    this.materielService.archiveMateriel(this.chantierCode, this.materiel.id, false).subscribe((unArchivedMateriel) => {
      this.loadMaterielInCard(unArchivedMateriel)
      this.toastr.success('Matériel désarchivé')
      reloadRoute(this.router, ['chantiers', this.chantierCode, 'a-receptionner'])
    })
  }

  openReceptionModal() {
    const modalRef = this.modalService.open(MaterielReceptionModalComponent)
    modalRef.componentInstance.materiel = this.materiel
    modalRef.componentInstance.chantierCode = this.chantierCode
  }

  updateDernierEvenementCSS() {
    if (!this.materiel) {
      this.dernierEvenementCSS = null
      return
    }
    const lastEvenement = this.materiel.dateDernierEvenement
    if (lastEvenement && this.materiel.statut !== 'HISTORISE') {
      const dateNow = new Date()
      const differenceBetweenNow = dayjs(dateNow).diff(lastEvenement, 'day')
      if (differenceBetweenNow >= 6) {
        this.dernierEvenementCSS = 'evenement-rouge'
        return
      } else if (dayjs(dateNow).isSame(lastEvenement, 'day')) {
        this.dernierEvenementCSS = 'evenement-vert'
        return
      }
    }
    this.dernierEvenementCSS = null
  }

  private loadMaterielInCard(materiel: Materiel) {
    this.materiel = {
      statut: materiel.getStatut(),
      dateDernierEvenement: materiel.getLastEvenement()?.date,
      uniteTemps: materiel.uniteTemps,
      dateReception: materiel.getDateReception(),
      nom: materiel.nom,
      archived: materiel.archived,
      alerteVGP: materiel.getAlerteVgp(),
      id: materiel.id,
      nomLocatier: materiel.locatier.nom,
      dateReceptionEstimee: materiel.dateReceptionEstimee,
      numParc: materiel.numParc,
      tarifLocation: materiel.tarifLocation,
      idPhotoMateriel: materiel.idPhotoMateriel,
      codeCategorie: materiel.categorie.code,
      idEvenementSortie: materiel.getEvenementSortie()?.id,
      numSerie: materiel.numSerie,
    }
  }

  private initCardPhoto() {
    if (!this.materiel) return

    if (this.photoSubscription) {
      this.photoSubscription.unsubscribe()
    }

    const photoId = this.materiel.idPhotoMateriel
    if (!photoId) {
      const currentCodeCategorie = this.materiel.codeCategorie
      this.photoSubscription = this.referenceService.getCategoriesMateriel().subscribe((categories) => {
        if (this.materiel && this.materiel.codeCategorie === currentCodeCategorie) {
          const materielCategorie = categories.find((cat) => cat.code === currentCodeCategorie)
          if (materielCategorie) {
            this.photoSubscription = this.photoService.getById(materielCategorie.photoCategorieId).subscribe((materielPhoto) => {
              this.materielPhotoUrl = materielPhoto.lambdaUrl
              this.cdr.markForCheck()
            })
          }
        }
      })
    } else {
      this.photoSubscription = this.photoService.getById(photoId).subscribe((materielPhoto) => {
        this.materielPhotoUrl = materielPhoto.lambdaUrl
        this.cdr.markForCheck()
      })
    }
  }
}
